<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="850px">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.training-budget.new-window.title")
                  : $t("wkt.admin.training-budget.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form">
              <v-row v-if="!isCorporateLicense">
                <v-col cols="12">
                  <v-alert dense outlined type="info" class="mb-4">
                    {{ $t("wkt.admin.common.license-alert.access.title") }}
                  </v-alert>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="data.reference"
                    :label="$t('wkt.admin.training-budget.edit-window.reference')"
                    autocomplete="off"
                    maxlength="100"
                    counter="100"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu ref="fromDateMenu" v-model="fromDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="fromDateFormatted"
                        :label="$t('wkt.admin.training-budget.edit-window.from-date')"
                        v-on="on"
                        v-bind="attrs"
                        readonly
                        :rules="[validationRules.required]"
                        :disabled="isReadOnly"
                        prepend-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker v-model="fromDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="fromDateMenu = false" />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu ref="toDateMenu" v-model="toDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="toDateFormatted"
                        :label="$t('wkt.admin.training-budget.edit-window.to-date')"
                        v-on="on"
                        v-bind="attrs"
                        readonly
                        :rules="[validationRules.required]"
                        :disabled="isReadOnly"
                        prepend-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker v-model="toDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="toDateMenu = false" />
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.totalBudget"
                    :label="$t('wkt.admin.training-budget.edit-window.total-budget')"
                    type="number"
                    step="0.01"
                    min="0"
                    :rules="[validationRules.required]"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.fundaeCredit"
                    :label="$t('wkt.admin.training-budget.edit-window.fundae-credit')"
                    type="number"
                    step="0.01"
                    min="0"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="data.id"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly"
          >
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.training-budget.edit-window.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.training-budget.edit-window.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import { Constants } from "../../common/Constants.js";

export default {
  name: "TrainingBudgetDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      data: {},
      profile: null,
      company: null,
      fromDate: null,
      toDate: null,
      fromDateMenu: false,
      toDateMenu: false,
      fromDateFormatted: null,
      toDateFormatted: null,
    };
  },
  computed: {
    sessionModuleTrainingBudget() {
      return this.profile?.moduleTrainingBudget;
    },
    isCorporateLicense() {
      return this.company?.licenseType === Constants.LICENSE_TYPE_CORPORATE;
    },
    userHasAnyAccess() {
      return this.sessionModuleTrainingBudget !== Constants.DENY_ALL;
    },
    isReadOnly() {
      return this.sessionModuleTrainingBudget === Constants.ALLOW_READ_ONLY || !this.isCorporateLicense;
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    open(action, payload) {
      this.profile = gwt.getSessionProfile();
      this.company = gwt.getSessionInformation()?.company || null;

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.showDialog = true;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.loading = true;

      if (!payload) {
        this.data = {
          companyId: this.profile.companyId,
        };
        this.fromDate = null;
        this.toDate = null;
        this.loading = false;
        return;
      }

      api.getTrainingBudgetById(
        payload,
        (res) => {
          this.data = res || {};
          this.fromDate = this.simpleDateToIso(this.data?.fromDate);
          this.toDate = this.simpleDateToIso(this.data?.toDate);
          this.loading = false;
        },
        (error) => {
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
          this.loading = false;
        },
        );
    },
    simpleDateToIso(value) {
      if (!value) {
        return null;
      }

      if (typeof value === "string") {
        return value;
      }

      const date = this.convertSimpleDateToDate(value);
      return date ? this.formatLocalDateToISOStringDate(date).split(", ")[0] : null;
    },
    isoDateToSimpleDate(value) {
      return value ? this.convertDateToSimpleDate(new Date(`${value}`)) : null;
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.training-budget.edit-window.date-range-error"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.loading = true;
      this.data.fromDate = this.isoDateToSimpleDate(this.fromDate);
      this.data.toDate = this.isoDateToSimpleDate(this.toDate);
      this.data.totalBudget =
        this.data.totalBudget === "" || this.data.totalBudget === null
          ? null
          : Number(this.data.totalBudget);
      this.data.fundaeCredit =
        this.data.fundaeCredit === "" || this.data.fundaeCredit === null
          ? null
          : Number(this.data.fundaeCredit);

      api.storeTrainingBudget(
        this.data,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    confirmDelete() {
      api.getTrainingBudgetUsageCount(
        this.data.id,
        (count) => {
          if (count > 0) {
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-budget.edit-window.associated_items_error", [
                count,
              ]),
              color: "error",
              timeout: 3000,
            });
            return;
          }
          this.$refs.dlgConfirm.open();
        },
        (error) => {
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    deleteItem() {
      this.loading = true;
      api.deleteTrainingBudget(
        this.data.id,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    updateDateFormatted() {
      this.fromDateFormatted = this.fromDate ? this.formatISOStringDateToLocalDate(this.fromDate) : null;
      this.toDateFormatted = this.toDate ? this.formatISOStringDateToLocalDate(this.toDate) : null;
    },
  },
  watch: {
    fromDate() { this.updateDateFormatted(); },
    toDate() { this.updateDateFormatted(); },
  },
};
</script>
